/* eslint-disable simple-import-sort/imports */
import React from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';

import { ConfirmationDialog } from '../../../confirmation-dialog/confirmation-dialog';
import { InfoBox } from '../../../info-box/info-box';
import { MultiSelectDropdown } from '../../../multi-select-dropdown/multi-select-dropdown';
import { Stack } from '../../../stack/stack';
import { useDialog } from '../../../../hooks/use-dialog';
import { ProjectContext } from '../../../../state/project-state/project-state';
import { SkyMapAxiosServiceFactory } from '../../../../../services/axios/skymap-axios-service-factory';
import { AssetManager } from '../../../../../viewer/asset-manager';
import { AssetTagModel } from '../../../../../../../typings/api/skymap/rest/v1/.common';
import { AssetTagChip } from './asset-tag-chip';
import { AssetTagDialog } from '../dialogs/asset-tag-dialog/asset-tag-dialog';
import { ManageTagsDialog } from '../dialogs/manage-tags-dialog';
import { Icon } from '../../../icon/icon';

type TaggableTarget = { kind: 'poi' | 'dynamicAsset'; id: string; name: string };

type Props = {
  target: TaggableTarget;
  initialTagsIds?: string[];
  refreshAsset?: () => void;
};

const AssetTags = (props: Props) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const confirmDialog = useDialog();
  const manageTagsDialog = useDialog();

  const { project, reloadAssetTags } = React.useContext(ProjectContext);
  const [assignedTags, setAssignedTags] = React.useState<AssetTagModel[]>(
    (props.initialTagsIds ?? [])
      .map((id) => project.assetTags?.find((t) => t.id === id))
      .filter((t) => t !== undefined) as AssetTagModel[],
  );
  const [associateError, setAssociateError] = React.useState(false);
  const [isExpanded] = React.useState(false);
  const [tagPendingRemoval, setTagPendingRemoval] = React.useState<AssetTagModel | null>(null);

  // Refs used to calculate how many tags fit into a single line next to the button
  const inlineContainerRef = React.useRef<HTMLDivElement | null>(null);
  const tagElementRefs = React.useRef<Record<string, HTMLSpanElement | null>>({});
  // const [visibleCount] = React.useState<number>(0);

  React.useEffect(() => {
    setAssignedTags(
      (props.initialTagsIds ?? [])
        .map((id) => project.assetTags?.find((t) => t.id === id))
        .filter((t) => t !== undefined) as AssetTagModel[],
    );
    setAssociateError(false);
  }, [props.target.id, props.initialTagsIds, project]);

  // const onAddTagClick = () => {
  //   dialog.show();
  // };

  // Compute how many tags fit in the inline row (to the right of the button)
  const recomputeVisibleCount = React.useCallback(() => {
    if (isExpanded) {
      // setVisibleCount(assignedTags.length);
      return;
    }

    const container = inlineContainerRef.current;
    if (!container) {
      return;
    }

    const containerWidth = container.clientWidth;
    if (containerWidth <= 0) {
      // setVisibleCount(0);
      return;
    }

    const gapPx = 6; // horizontal gap between pills
    const overflowButtonApproxWidth = 44; // approximate width for +N… button

    const tagWidths = assignedTags.map(
      (t) => tagElementRefs.current[t.id]?.getBoundingClientRect().width ?? 0,
    );

    let usedWidth = 0;
    let count = 0;
    for (let i = 0; i < tagWidths.length; i += 1) {
      const tagWidth = tagWidths[i];
      const isFirst = count === 0;
      const widthWithGap = (isFirst ? 0 : gapPx) + tagWidth;
      const remainingAfter = tagWidths.length - (i + 1);
      const requireOverflowButton = remainingAfter > 0;
      const projected =
        usedWidth + widthWithGap + (requireOverflowButton ? gapPx + overflowButtonApproxWidth : 0);

      if (projected <= containerWidth) {
        usedWidth += widthWithGap;
        count += 1;
      } else {
        break;
      }
    }

    // setVisibleCount(count);
  }, [assignedTags, isExpanded]);

  // Recompute on data changes and when the container resizes
  React.useLayoutEffect(() => {
    recomputeVisibleCount();
    const ResizeObserverCtor = typeof window !== 'undefined' ? window.ResizeObserver : undefined;
    const ro = ResizeObserverCtor
      ? new ResizeObserverCtor(() => {
          recomputeVisibleCount();
        })
      : undefined;
    const el = inlineContainerRef.current;
    if (el && ro) {
      ro.observe(el);
    }
    const onWinResize = () => recomputeVisibleCount();
    window.addEventListener('resize', onWinResize);
    return () => {
      if (ro && el) {
        ro.unobserve(el);
        ro.disconnect();
      }
      window.removeEventListener('resize', onWinResize);
    };
  }, [recomputeVisibleCount]);

  return (
    <>
      <Stack spacing={0.5}>
        <MultiSelectDropdown
          allowCreateNew={true}
          dropdownFooter={
            <FooterRow
              role="button"
              tabIndex={0}
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                e.preventDefault();
                manageTagsDialog.show();
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  manageTagsDialog.show();
                }
              }}
            >
              {t('manageTagsDialog.title', { ns: 'skyviewAssetMenu' })}
            </FooterRow>
          }
          id="assetTagsEditor"
          isExternalDialogOpen={manageTagsDialog.visible}
          options={(project.assetTags ?? []).map((t) => ({ id: t.id, name: t.name }))}
          placeholder={t('tagDialog.placeholder', { ns: 'skyviewAssetMenu' })}
          renderOption={(opt) => <AssetTagChip tag={{ id: opt.id }} />}
          renderOptionAction={() => (
            <Icon icon={['fas', 'cog']} onClick={() => manageTagsDialog.show()} />
          )}
          renderSelectedChip={(opt, onRemove) => (
            <AssetTagChip tag={{ id: opt.id }} onClose={onRemove} />
          )}
          searchPlaceholder={t('tagDialog.searchOrCreatePlaceholder', { ns: 'skyviewAssetMenu' })}
          selectedIds={assignedTags.map((t) => t.id)}
          suggestionsLimit={8}
          onChange={(ids) => {
            const currentIds = new Set(assignedTags.map((t) => t.id));
            const newIds = new Set(ids);
            const toAdd: string[] = [];
            const toRemove: string[] = [];

            for (const id of newIds) {
              if (!currentIds.has(id)) {
                toAdd.push(id);
              }
            }
            for (const id of currentIds) {
              if (!newIds.has(id)) {
                toRemove.push(id);
              }
            }

            try {
              setAssociateError(false);
              // Apply additions
              void (async () => {
                for (const id of toAdd) {
                  if (props.target.kind === 'poi') {
                    await SkyMapAxiosServiceFactory.instance
                      .createPoiServiceV1()
                      .associateAssetTagWithPoi({
                        path: { poiId: props.target.id, assetTagId: id },
                      });
                  } else {
                    await SkyMapAxiosServiceFactory.instance
                      .createDynamicAssetServiceV0()
                      .associateAssetTagWithDynamicAsset({
                        path: { dynamicAssetId: props.target.id, assetTagId: id },
                      });
                  }
                }

                // Apply removals
                for (const id of toRemove) {
                  if (props.target.kind === 'poi') {
                    await SkyMapAxiosServiceFactory.instance
                      .createPoiServiceV1()
                      .removeAssociatedAssetTagFromPoi({
                        path: { poiId: props.target.id, assetTagId: id },
                      });
                  } else {
                    await SkyMapAxiosServiceFactory.instance
                      .createDynamicAssetServiceV0()
                      .removeAssociatedAssetTagFromDynamicAsset({
                        path: { dynamicAssetId: props.target.id, assetTagId: id },
                      });
                  }
                }

                // Try to refresh underlying asset without affecting UI in case of errors
                try {
                  await AssetManager.instance.updateAsset(
                    props.target.id,
                    props.target.kind === 'poi' ? 'Poi' : 'DynamicAsset',
                  );
                  props.refreshAsset?.();
                } catch {
                  // ignore refresh errors in UI
                }
              })();

              // Update local UI
              const next = (project.assetTags ?? []).filter((t) => newIds.has(t.id));
              setAssignedTags(next);
            } catch (err) {
              setAssociateError(true);
            }
          }}
          onCreateNew={async (name) => {
            try {
              const { data } = await SkyMapAxiosServiceFactory.instance
                .createProjectServiceV1()
                .createAssetTag({ path: { projectId: project.id }, body: { name } });
              await reloadAssetTags();
              return { id: data.id, name: data.name };
            } catch {
              return undefined;
            }
          }}
        />
      </Stack>

      {/* Deprecated expanded row (replaced by grid layout above) */}

      {associateError && (
        <InfoBox color="red">
          <Trans
            components={{ br: <br /> }}
            i18nKey="fileManager.folderTagsDialog.unknownError"
            ns="components"
          />
        </InfoBox>
      )}

      {dialog.render(
        <AssetTagDialog
          excludeTagIds={assignedTags.map((t) => t.id)}
          onClose={(addedTag) => {
            dialog.hide();
            if (addedTag) {
              void (async () => {
                try {
                  setAssociateError(false);
                  if (props.target.kind === 'poi') {
                    await SkyMapAxiosServiceFactory.instance
                      .createPoiServiceV1()
                      .associateAssetTagWithPoi({
                        path: { poiId: props.target.id, assetTagId: addedTag.id },
                      });
                  } else {
                    await SkyMapAxiosServiceFactory.instance
                      .createDynamicAssetServiceV0()
                      .associateAssetTagWithDynamicAsset({
                        path: { dynamicAssetId: props.target.id, assetTagId: addedTag.id },
                      });
                  }

                  // Update local UI immediately
                  setAssignedTags((prev) => {
                    if (prev.some((t) => t.id === addedTag.id)) {
                      return prev;
                    }
                    return [...prev, addedTag];
                  });

                  // Try to refresh underlying asset without affecting UI in case of errors
                  void (async () => {
                    try {
                      await AssetManager.instance.updateAsset(
                        props.target.id,
                        props.target.kind === 'poi' ? 'Poi' : 'DynamicAsset',
                      );
                      props.refreshAsset?.();
                    } catch {
                      // ignore refresh errors in UI
                    }
                  })();
                } catch (err) {
                  setAssociateError(true);
                }
              })();
            }
          }}
        />,
      )}

      {confirmDialog.render((pos) => (
        <ConfirmationDialog
          negative={true}
          pos={pos}
          title={`${t('delete', { ns: 'common' })} ${
            (project.assetTags ?? []).find((p) => p.id === tagPendingRemoval?.id)?.name ??
            tagPendingRemoval?.name ??
            ''
          }?`}
          onCancel={() => {
            setTagPendingRemoval(null);
            confirmDialog.hide();
          }}
          onConfirm={async () => {
            const tag = tagPendingRemoval;
            if (!tag) {
              confirmDialog.hide();
              return;
            }
            try {
              setAssociateError(false);
              if (props.target.kind === 'poi') {
                await SkyMapAxiosServiceFactory.instance
                  .createPoiServiceV1()
                  .removeAssociatedAssetTagFromPoi({
                    path: { poiId: props.target.id, assetTagId: tag.id },
                  });
              } else {
                await SkyMapAxiosServiceFactory.instance
                  .createDynamicAssetServiceV0()
                  .removeAssociatedAssetTagFromDynamicAsset({
                    path: { dynamicAssetId: props.target.id, assetTagId: tag.id },
                  });
              }

              setAssignedTags((prev) => prev.filter((t) => t.id !== tag.id));

              void (async () => {
                try {
                  await AssetManager.instance.updateAsset(
                    props.target.id,
                    props.target.kind === 'poi' ? 'Poi' : 'DynamicAsset',
                  );
                  props.refreshAsset?.();
                } catch {
                  // ignore refresh errors in UI
                }
              })();
            } catch (err) {
              setAssociateError(true);
            } finally {
              setTagPendingRemoval(null);
              confirmDialog.hide();
            }
          }}
        >
          <Trans
            components={{ bold: <strong /> }}
            i18nKey="assetTags.removeConfirm"
            ns="components"
            values={{
              tagName:
                (project.assetTags ?? []).find((p) => p.id === tagPendingRemoval?.id)?.name ??
                tagPendingRemoval?.name ??
                '',
              resourceName: props.target.name,
            }}
          />
        </ConfirmationDialog>
      ))}

      {manageTagsDialog.render(
        <ManageTagsDialog
          onClose={() => {
            manageTagsDialog.hide();
          }}
        />,
      )}
    </>
  );
};

export { AssetTags };

// Removed nowrap scroller; expanded view now wraps below

const FooterRow = styled.div`
  cursor: pointer;
  color: ${({ theme }) => theme.color.blue};
  padding: 0.5em;
  &:hover {
    text-decoration: underline;
  }
`;
